import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Save, Search, Package } from "lucide-react";
import { returnsAPI, suppliersAPI, productsAPI, getApiErrorPayload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { FieldError } from "@/components/ui/field-error";

interface ReturnItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	barcode?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage: number;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
}

interface ReturnForm {
	supplierId: string;
	returnDate: string;
	status: "pending" | "approved" | "rejected" | "completed";
	paymentStatus: "unpaid" | "partial" | "paid";
	taxAmount: number;
	notes: string;
}

interface ReturnUpdatePayload {
	status?: "PENDING" | "APPROVED" | "REJECTED" | "PROCESSED" | "CANCELLED";
	paymentStatus?: "PENDING" | "PARTIAL" | "COMPLETED" | "REFUNDED";
	returnDate?: string;
	supplierId?: number;
	notes?: string;
	items: Array<{
		productId: number;
		quantity: number;
		unitPrice: number;
		discountPercentage?: number;
		taxPercentage?: number;
	}>;
}

const ReturnEditPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const currency = useCurrency();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [suppliers, setSuppliers] = useState<any[]>([]);
	const [products, setProducts] = useState<any[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<ReturnItem[]>([]);
	const [returnForm, setReturnForm] = useState<ReturnForm>({
		supplierId: "",
		returnDate: new Date().toISOString().split("T")[0],
		status: "pending",
		paymentStatus: "unpaid",
		taxAmount: 0,
		notes: "",
	});

	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const { isDirty } = useIsDirty({ returnForm, selectedProducts }, !loading);
	const { allowNavigation } = useUnsavedChangesGuard(isDirty && !saving);

	const clearFieldError = (field: string) => {
		setFieldErrors(prev => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	useEffect(() => {
		if (id) {
			fetchReturn();
			fetchSuppliers();
			fetchProducts();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredProducts(products);
		} else {
			const filtered = products.filter(
				product =>
					product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					product.barcode?.toLowerCase().includes(searchQuery.toLowerCase()),
			);
			setFilteredProducts(filtered);
		}
	}, [searchQuery, products]);

	const fetchReturn = async () => {
		try {
			setLoading(true);
			const data = await returnsAPI.getById(id!);
			setReturnForm({
				supplierId: data.supplierId || "",
				returnDate: data.returnDate || new Date().toISOString().split("T")[0],
				status: data.status || "pending",
				paymentStatus: data.paymentStatus || "unpaid",
				taxAmount: data.taxAmount || 0,
				notes: data.notes || "",
			});
			const items: ReturnItem[] = (data.items || []).map((item: any) => ({
				id: String(item.id),
				productId: item.productId,
				productName: item.product?.name ?? item.productName ?? "",
				productImage: item.product?.image ?? item.productImage,
				barcode: item.product?.barcode ?? item.barcode,
				quantity: Number(item.quantity) || 0,
				unitPrice: Number(item.unitPrice) || 0,
				discountPercentage: Number(item.discountPercentage) ?? 0,
				discountAmount: Number(item.discountAmount) ?? 0,
				taxPercentage: Number(item.taxPercentage) ?? 0,
				taxAmount: Number(item.taxAmount) ?? 0,
				totalAmount: Number(item.totalAmount) ?? 0,
			}));
			setSelectedProducts(items);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("returns.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/returns");
		} finally {
			setLoading(false);
		}
	};

	const fetchSuppliers = async () => {
		try {
			const data = await suppliersAPI.getAll({ page: 1, limit: 1000 });
			setSuppliers(data.data || []);
		} catch (error) {
			console.error("Failed to fetch suppliers:", error);
		}
	};

	const fetchProducts = async () => {
		try {
			const data = await productsAPI.getAll({ page: 1, limit: 1000 });
			setProducts(data.data || []);
			setFilteredProducts(data.data || []);
		} catch (error) {
			console.error("Failed to fetch products:", error);
		}
	};

	const handleInputChange = (field: keyof ReturnForm, value: any) => {
		setReturnForm(prev => ({ ...prev, [field]: value }));
		clearFieldError(field);
	};

	const addProductToReturn = (product: any) => {
		clearFieldError("items");
		const existingItem = selectedProducts.find(item => item.productId === product.id);

		if (existingItem) {
			setSelectedProducts(prev =>
				prev.map(item => (item.productId === product.id ? { ...item, quantity: item.quantity + 1 } : item)),
			);
		} else {
			const newItem: ReturnItem = {
				id: Date.now().toString(),
				productId: product.id,
				productName: product.name,
				productImage: product.image,
				barcode: product.barcode,
				quantity: 1,
				unitPrice: product.purchasePrice || 0,
				discountPercentage: 0,
				discountAmount: 0,
				taxPercentage: product.taxPercentage || 0,
				taxAmount: 0,
				totalAmount: product.purchasePrice || 0,
			};
			setSelectedProducts(prev => [...prev, newItem]);
		}

		setSearchQuery("");
		setFilteredProducts(products);
	};

	const updateItem = (itemId: string, field: keyof ReturnItem, value: any) => {
		setSelectedProducts(prev =>
			prev.map(item => {
				if (item.id === itemId) {
					const updatedItem = { ...item, [field]: value };

					const discountAmount =
						(updatedItem.unitPrice * updatedItem.quantity * updatedItem.discountPercentage) / 100;
					const taxAmount =
						((updatedItem.unitPrice * updatedItem.quantity - discountAmount) * updatedItem.taxPercentage) /
						100;
					const totalAmount = updatedItem.unitPrice * updatedItem.quantity - discountAmount + taxAmount;

					return {
						...updatedItem,
						discountAmount,
						taxAmount,
						totalAmount,
					};
				}
				return item;
			}),
		);
	};

	const removeItem = (itemId: string) => {
		setSelectedProducts(prev => prev.filter(item => item.id !== itemId));
	};

	const calculateTotals = () => {
		const subtotal = selectedProducts.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
		const totalDiscount = selectedProducts.reduce((sum, item) => sum + item.discountAmount, 0);
		const totalTax = selectedProducts.reduce((sum, item) => sum + item.taxAmount, 0);
		const total = subtotal - totalDiscount + totalTax;

		return { subtotal, totalDiscount, totalTax, total };
	};

	const mapReturnStatus = (status: ReturnForm["status"]): ReturnUpdatePayload["status"] => {
		if (status === "approved") return "APPROVED";
		if (status === "rejected") return "REJECTED";
		if (status === "completed") return "PROCESSED";
		return "PENDING";
	};

	const mapReturnPaymentStatus = (status: ReturnForm["paymentStatus"]): ReturnUpdatePayload["paymentStatus"] => {
		if (status === "partial") return "PARTIAL";
		if (status === "paid") return "COMPLETED";
		return "PENDING";
	};

	const handleSubmit = async () => {
		const errors: Record<string, string> = {};
		if (!returnForm.supplierId) errors.supplierId = "returns.errors.supplierRequired";
		if (selectedProducts.length === 0) errors.items = "returns.errors.productsRequired";
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			toast({
				title: t("common.error"),
				description: t("validation.checkFields"),
				variant: "destructive",
			});
			return;
		}
		setFieldErrors({});

		try {
			setSaving(true);
			const returnData = {
				supplierId: returnForm.supplierId ? Number(returnForm.supplierId) : undefined,
				returnDate: returnForm.returnDate || undefined,
				status: mapReturnStatus(returnForm.status),
				paymentStatus: mapReturnPaymentStatus(returnForm.paymentStatus),
				notes: returnForm.notes || undefined,
				items: selectedProducts.map(item => ({
					productId: Number(item.productId),
					quantity: Number(item.quantity) || 0,
					unitPrice: Number(item.unitPrice) || 0,
					discountPercentage: Number(item.discountPercentage) || 0,
					taxPercentage: Number(item.taxPercentage) || 0,
				})),
			} satisfies ReturnUpdatePayload;

			await returnsAPI.update(id!, returnData);
			toast({
				title: t("common.success"),
				description: t("returns.messages.updated"),
			});
			allowNavigation();
			navigate(`/returns/${id}`);
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const { subtotal, totalDiscount, totalTax, total } = calculateTotals();

	if (loading) return <Loading />;

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("returns.edit.title")}</PageHeading>
						<p className="text-muted-foreground">{t("returns.edit.description")}</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Button variant="outline" onClick={() => navigate(`/returns/${id}`)} disabled={saving}>
						{t("common.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={saving}>
						{saving ? (
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
						) : (
							<Save className="h-4 w-4 mx-2" />
						)}
						{t("common.save")}
					</Button>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("returns.edit.operationData")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>
										{t("returns.edit.supplier")} <span className="text-destructive">*</span>
									</Label>
									<Select
										value={returnForm.supplierId}
										onValueChange={value => handleInputChange("supplierId", value)}
									>
										<SelectTrigger
											aria-label={t("returns.edit.supplier")}
											aria-invalid={!!fieldErrors.supplierId}
										>
											<SelectValue placeholder={t("returns.edit.selectSupplier")} />
										</SelectTrigger>
										<SelectContent>
											{suppliers.map(supplier => (
												<SelectItem key={supplier.id} value={String(supplier.id)}>
													{supplier.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FieldError message={fieldErrors.supplierId} />
								</div>
								<div className="space-y-2">
									<Label>{t("returns.edit.returnDate")}</Label>
									<Input
										aria-label={t("returns.edit.returnDate")}
										type="date"
										value={returnForm.returnDate}
										onChange={e => handleInputChange("returnDate", e.target.value)}
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>{t("returns.edit.status")}</Label>
									<Select
										value={returnForm.status}
										onValueChange={value => handleInputChange("status", value)}
									>
										<SelectTrigger aria-label={t("returns.edit.status")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="pending">{t("returns.status.pending")}</SelectItem>
											<SelectItem value="approved">{t("returns.status.approved")}</SelectItem>
											<SelectItem value="rejected">{t("returns.status.rejected")}</SelectItem>
											<SelectItem value="completed">{t("returns.status.completed")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>{t("returns.edit.paymentStatus")}</Label>
									<Select
										value={returnForm.paymentStatus}
										onValueChange={value => handleInputChange("paymentStatus", value)}
									>
										<SelectTrigger aria-label={t("returns.edit.paymentStatus")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="unpaid">{t("returns.paymentStatus.unpaid")}</SelectItem>
											<SelectItem value="partial">
												{t("returns.paymentStatus.partial")}
											</SelectItem>
											<SelectItem value="paid">{t("returns.paymentStatus.paid")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>

							<div className="space-y-2">
								<Label>{t("returns.edit.notes")}</Label>
								<Input
									aria-label={t("returns.edit.notes")}
									type="text"
									value={returnForm.notes}
									onChange={e => handleInputChange("notes", e.target.value)}
									placeholder={t("returns.edit.notesPlaceholder")}
								/>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2 text-base">
								<Package className="h-5 w-5" />
								{t("returns.edit.products")}
							</CardTitle>
							<CardDescription>{t("returns.edit.productsDescription")}</CardDescription>
						</CardHeader>
						<CardContent className="space-y-6">
							<div className="space-y-2">
								<Label>{t("returns.edit.searchProducts")}</Label>
								<div className="relative">
									<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
									<Input
										aria-label={t("returns.edit.searchProducts")}
										placeholder={t("returns.edit.searchPlaceholder")}
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
										className="ps-10"
									/>
								</div>
							</div>

							<div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{filteredProducts.map(product => (
										<div
											key={product.id}
											className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer"
											onClick={() => addProductToReturn(product)}
										>
											<div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
												{product.image ? (
													<img
														src={product.image}
														alt={product.name}
														className="w-full h-full object-cover rounded-lg"
													/>
												) : (
													<Package className="h-6 w-6 text-primary" />
												)}
											</div>
											<div className="flex-1">
												<h4 className="font-medium">{product.name}</h4>
												<p className="text-sm font-medium">
													<Price value={product.purchasePrice || 0} currency={currency} />
												</p>
											</div>
											<Button
												title={t("common.add")}
												aria-label={t("common.add")}
												size="sm"
												variant="outline"
											>
												<Plus className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
							</div>

							<div className="space-y-4">
								<h4 className="font-medium">
									{t("returns.edit.selectedProducts")} <span className="text-destructive">*</span>
								</h4>
								<FieldError message={fieldErrors.items} />
								{selectedProducts.map(item => (
									<div
										key={item.id}
										className="flex flex-col items-stretch gap-4 p-4 border rounded-lg md:flex-row md:items-center"
									>
										<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
											{item.productImage ? (
												<img
													src={item.productImage}
													alt={item.productName}
													className="w-full h-full object-cover rounded-lg"
												/>
											) : (
												<Package className="h-8 w-8 text-primary" />
											)}
										</div>
										<div className="flex-1">
											<h4 className="font-medium">{item.productName}</h4>
											<div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mt-2">
												<div>
													<Label className="text-sm">{t("returns.edit.quantity")}</Label>
													<Input
														aria-label={t("returns.edit.quantity")}
														type="number"
														min="1"
														value={item.quantity}
														onChange={e =>
															updateItem(
																item.id,
																"quantity",
																parseInt(e.target.value) || 1,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("returns.edit.unitPrice")}</Label>
													<Input
														aria-label={t("returns.edit.unitPrice")}
														type="number"
														min="0"
														step="0.01"
														value={item.unitPrice}
														onChange={e =>
															updateItem(
																item.id,
																"unitPrice",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-24"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("returns.edit.discount")} </Label>
													<Input
														aria-label={t("returns.edit.discount")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.discountPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"discountPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("returns.edit.total")}</Label>
													<div className="text-lg font-semibold">
														<Price value={item.totalAmount} currency={currency} />
													</div>
												</div>
											</div>
										</div>
										<Button
											title={t("common.delete")}
											aria-label={t("common.delete")}
											size="sm"
											variant="destructive"
											onClick={() => removeItem(item.id)}
										>
											<Trash2 className="h-4 w-4 text-white" />
										</Button>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</div>

				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("returns.edit.summary")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex justify-between">
								<span>{t("returns.edit.subtotal")}</span>
								<span className="font-semibold">
									<Price value={subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("returns.edit.discount")}</span>
								<span className="font-semibold text-destructive">
									-<Price value={totalDiscount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("returns.edit.tax")}</span>
								<span className="font-semibold">
									<Price value={totalTax} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("returns.edit.total")}</span>
									<span>
										<Price value={total} currency={currency} />
									</span>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default ReturnEditPage;
